import { assertNever } from "@glideapps/ts-necessities";
import { makeRowID } from "../make-row-id";
import { HeroSize, HeroStyle, HeroImageEffect, DefaultImage } from "../wire";
import { WireComponentKind } from "../wire/component-kind";
import { AppDescription, AppKind, getAppKind } from "../components/SerializedApp";
import { assert } from "../Support";
import { HeroSectionDescription } from "./components/fluent-components";
import { Corners } from "./components/image-types";
import {
    getTableProperty,
    makeEnumProperty,
    makeSpecialValueProperty,
    makeSwitchProperty,
} from "./components/property-utils";
import { ComponentKindUserProfile, UserProfileComponentDescription } from "./components/user-profile";
import {
    ClassScreenDescription,
    ComponentDescription,
    ScreenDescriptionKind,
    SpecialValueKind,
    TypeSchema,
    UserProfileDescription,
    findTable,
    makeTableRef,
    userProfileScreenName,
    ScreenDescription,
    makeEmptyComponentDescription,
} from "./description";

function makeUserProfileComponent(appKind: AppKind, userProfileData: UserProfileDescription): ComponentDescription {
    if (appKind === AppKind.App) {
        const component: UserProfileComponentDescription = {
            ...makeEmptyComponentDescription(ComponentKindUserProfile),
            titleProperty: userProfileData.nameColumn,
            subtitleProperty: makeSpecialValueProperty(SpecialValueKind.RealEmailAddress),
            imageURLProperty: userProfileData.imageColumn,
            allowImageUpload: userProfileData.allowImageUpload,
        };
        return component;
    } else if (appKind === AppKind.Page) {
        const component: HeroSectionDescription = {
            kind: WireComponentKind.Hero,
            title: userProfileData.nameColumn,
            subtitle: makeSpecialValueProperty(SpecialValueKind.RealEmailAddress),
            image: userProfileData.imageColumn,
            size: makeEnumProperty(HeroSize.Medium),
            style: makeEnumProperty(HeroStyle.Card),
            backgroundEffect: makeEnumProperty(HeroImageEffect.None),
            withBlur: makeSwitchProperty(false),
            imageCorners: makeEnumProperty(Corners.Rounded),
            imageDefault: makeEnumProperty(DefaultImage.None),
            componentID: makeRowID(),
            visibilityFilters: undefined,
        };
        return component;
    } else {
        return assertNever(appKind);
    }
}

export function makeUserProfileScreen(
    userProfileData: UserProfileDescription | undefined,
    schema: TypeSchema,
    keepComponents: boolean,
    appDescription: AppDescription
): ClassScreenDescription | undefined {
    if (userProfileData === undefined) return undefined;

    const table = findTable(schema, getTableProperty(userProfileData?.userProfileTable));
    if (table === undefined) return undefined;

    const userComponent = makeUserProfileComponent(getAppKind(appDescription), userProfileData);
    const components: ComponentDescription[] = [userComponent];

    if (keepComponents && appDescription !== undefined) {
        const oldScreen: ScreenDescription | undefined = appDescription.screenDescriptions[userProfileScreenName];
        if (oldScreen !== undefined) {
            assert(oldScreen.kind === ScreenDescriptionKind.Class);
            const oldComponents = oldScreen.components.filter(c => c.kind !== ComponentKindUserProfile);
            components.push(...oldComponents);
        }
    }

    return {
        kind: ScreenDescriptionKind.Class,
        type: makeTableRef(table),
        canEdit: false,
        canEditFilters: [],
        canDelete: false,
        canDeleteFilters: [],
        searchPlaceholder: undefined,
        isForm: undefined,
        fetchesData: true,
        title: undefined,
        transforms: [],
        components,
    };
}
